import * as IoC from 'typescript-ioc';
import React from 'react';
import { action, observable, runInAction } from 'mobx';
import { AbstractFormViewModelController } from '../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { configList, FieldKey } from './form.config';
import { FormVmValueType, InitialData } from '../../../../../../infrastructure/form-viewmodel-controller';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { FlyoutTabOption, IConnectionDetailsDTO, IConnectionsDAO, ICreateKnovioSubscriptionPayload, IKnovioEditMetadata, IKnovioTemplateOptions, IKnovioLiveStreamDTO, LayoutOption, KnovioLabel } from '../../../../../../api/connections.dao';
import { IAttendeeOrganizationDTO } from '../../../../../../api/attendees.dao';
import { DateTimeFormatter } from '../../../../../../infrastructure/implementations/date-formatter';
import { DateFnsFormats } from '../../../../../../infrastructure/date-formatter';
import { alertServerError, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { ApiDateConverter } from '../../../../../../infrastructure/implementations/api-date-converter';
import { MeetingApiParams } from '../_connection-form/connection-form.viewmodel';
import { IAppsyncSubscription, subscribeAppsyncData } from '../../../../../../infrastructure/utils/appsync.utils';
import { onSyncEvent, onUpdateMeetingConnection } from '../../../../../../graphql/subscriptions';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS, convertBoolTypeForMixPanel, isTrackingFieldEnabled } from '../../../../../../tracking-service/tracking-values';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import { getCommaSeparatedStringList } from '../../../../../../infrastructure/utils/common.utils';
import { NOT_AVAILABLE_STRING } from '../../../../../../constants/common.constants';
import { IOnSyncEventSubscriptionResponse, OnSyncEventType } from '../../../../../../api/events.dao';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../global/rich-text-editor/utils/transpiler';


export enum KnovioLiveStreamVariant {
    CREATE = 'create',
    EDIT = 'edit'
}

export interface KnovioLiveStreamVMProps {
    startDateTime?: Date;
    timeZone?: string;
    presenterOrganizationList?: IAttendeeOrganizationDTO[];
    meetingTopic?: string;
    meetingApiParams?: MeetingApiParams;
}

export type FormFieldProps = {
    value: FormVmValueType;
    valid: boolean;
    touched: boolean;
    validationMessage: MessageDescriptor | null;
};

export class KnovioLiveStreamVM {
    private static createFormVM(initialData: InitialData): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'KnovioLiveStreamVM', configList);
            }
        }();
    }

    @observable
    private formDataCtrl: AbstractFormViewModelController | null = null;
    private behaviorVariant: KnovioLiveStreamVariant = KnovioLiveStreamVariant.CREATE;
    private knovioConnectionCallback: (connectionDetails: IConnectionDetailsDTO) => void;
    private knovioAfterUpdateCallback: (dto: IKnovioLiveStreamDTO) => void;
    private subscriptions: Set<IAppsyncSubscription> = new Set();
    private updatingKnovioInProgress: Set<number> = new Set();
    private updatingKnovioSubscriptions: Set<IAppsyncSubscription> = new Set();
    private knovioMetadataDetails: IKnovioEditMetadata;
    private meetingConnectionId: number;
    private knovioPresentationId: number;
    // Used in case we don't have start date in props
    private meetingStartDate: Date | null = null;

    public get formVM(): AbstractFormViewModelController | null {
        return this.formDataCtrl;
    }

    public get behavior(): KnovioLiveStreamVariant {
        return this.behaviorVariant;
    }


    @observable
    public isOpened: boolean = false;
    @observable
    public isCreatingKnovio = false;
    @observable
    public isUpdatingKnovio = false;

    @action.bound
    public openModal(): void {
        if (this.behavior === KnovioLiveStreamVariant.CREATE) {
            this.initKnovioCreateFormDataController();
        } else {
            this.initKnovioEdit();
        }
        this.isOpened = true;
    }

    @action.bound
    public closeModal(): void {
        this.destroyFormDataController();
        this.isOpened = false;
        this.meetingStartDate = null;
        this.isUpdatingKnovio = false;
    }


    @action.bound
    public updateFlyoutTabsOptions(option: FlyoutTabOption): void {
        const options = [...this.formDataCtrl.getValue(FieldKey.FLYOUT_TABS) as FlyoutTabOption[]];

        const idx = options.indexOf(option);

        if (idx === -1) {
            options.push(option);
        } else {
            options.splice(idx, 1);
        }

        this.formDataCtrl.setValue(FieldKey.FLYOUT_TABS, options);
    }

    public get timeZone(): string {
        return this.props.timeZone;
    }

    public get startDateTime(): Date {
        return this.props.startDateTime;
    }

    public get flyoutTabsOptions(): { label: string; value: FlyoutTabOption }[] {
        return [
            { label: 'Overview', value: FlyoutTabOption.Overview },
            { label: 'Q&A', value: FlyoutTabOption.QuestionsAndAnswers },
            { label: 'Speakers', value: FlyoutTabOption.Speakers }
        ];
    }

    public get layoutOptions(): { label: string; value: LayoutOption }[] {
        return [
            { label: 'Video Only', value: LayoutOption.VideoOnly },
            { label: 'Video Left', value: LayoutOption.VideoLeft },
            { label: 'Video Right', value: LayoutOption.VideoRight },
            { label: 'Audio & Slides', value: LayoutOption.AudioAndSlides },
        ];
    }

    public getFormFieldPropsByKey(key: FieldKey): FormFieldProps {
        return {
            value: this.formDataCtrl.getValue(key),
            ...this.formDataCtrl.getValueValidationState(key)
        };
    }

    public openKnovioConnectionForm(connectionDetails: IConnectionDetailsDTO): void {
        this.closeModal();
        this.knovioConnectionCallback(connectionDetails);
    }

    public setBehavior(behavior: KnovioLiveStreamVariant): void {
        this.behaviorVariant = behavior;
    }

    public setKnovioMetadata(knovioMetadataDetails: IKnovioEditMetadata): void {
        this.knovioMetadataDetails = knovioMetadataDetails;
    }

    public setMeetingStartDate(date: Date): void {
        this.meetingStartDate = date;
    }

    public setKnovioConnectionCallback(callback: (connectionDetails: IConnectionDetailsDTO) => void): void {
        this.knovioConnectionCallback = callback;
    }

    public setKnovioAfterUpdateCallback(callback: (dto: IKnovioLiveStreamDTO) => void): void {
        this.knovioAfterUpdateCallback = callback;
    }

    public setMeetingConnectionId(id: number): void {
        this.meetingConnectionId = id;
    }

    public setKnovioPresentationId(id: number): void {
        this.knovioPresentationId = id;
    }

    constructor(
        private readonly props: KnovioLiveStreamVMProps,
        @IoC.Inject private readonly dateTimeFormatter: DateTimeFormatter = IoC.Container.get(DateTimeFormatter),
        @IoC.Inject private readonly apiDateConverter: ApiDateConverter = IoC.Container.get(ApiDateConverter),
        @IoC.Inject private readonly connectionsDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
    ) { }

    private initKnovioEdit(): void {
        if (this.updatingKnovioInProgress.has(this.knovioPresentationId)) {
            this.isUpdatingKnovio = true;
        }
        this.initEditKnovioFormDataController();
    }

    private initKnovioCreateFormDataController(): void {
        const props = this.composeKnovioCreateFormVMInitialProps();
        this.formDataCtrl = KnovioLiveStreamVM.createFormVM(props);
    }

    private initEditKnovioFormDataController(): void {
        const props = this.composeKnovioEditFormVMInitialProps();
        this.formDataCtrl = KnovioLiveStreamVM.createFormVM(props);
    }

    private destroyFormDataController(): void {
        this.formDataCtrl = null;
    }

    private composeKnovioEditFormVMInitialProps(): InitialData {
        const parseKnovioTemplateOptions = (templateOptions: IKnovioTemplateOptions): FlyoutTabOption[] => {
            if (!templateOptions) return [];

            const lookup = {
                enableOverview: FlyoutTabOption.Overview,
                enableQAndA: FlyoutTabOption.QuestionsAndAnswers,
                enableSpeakers: FlyoutTabOption.Speakers
            };

            return Object.keys(lookup).reduce((acc, key) => {
                if (templateOptions[key]) {
                    acc.push(lookup[key]);
                }
                return acc;
            }, []);
        };

        const parseExpirationDate = (metadata: IKnovioEditMetadata['metadata']): Record<string, unknown> => {
            const current = {};

            if (metadata.expirationDate) {
                current[FieldKey.EXPIRATION_DATE_ENABLED] = true;
                current[FieldKey.EXPIRATION_DATE] = new Date(metadata.expirationDate);
            };

            return current;
        };

        return {
            [FieldKey.BROADCAST_DATE_TIME]: this.getFormattedBroadcastDateTime() ,
            [FieldKey.QA_NOTIFICATION_EMAIL]: this.knovioMetadataDetails.qaNotificationEmailRecipients,
            [FieldKey.TOPIC]: this.knovioMetadataDetails.metadata.title,
            [FieldKey.DESCRIPTION]: this.knovioMetadataDetails.metadata.description || '',
            [FieldKey.NEVER_LATE_NAVIGATION]: this.knovioMetadataDetails.playbackSettings.enableDvrNavigation,
            [FieldKey.STANDBY_MESSAGE]: transpileInlineStylesToClasses(this.knovioMetadataDetails.metadata.standByMessage),
            [FieldKey.FLYOUT_TABS]: parseKnovioTemplateOptions(this.knovioMetadataDetails.template),
            [FieldKey.LAYOUT]: this.knovioMetadataDetails.layout || LayoutOption.VideoOnly,
            ...parseExpirationDate(this.knovioMetadataDetails.metadata)

        };
    }

    private composeKnovioCreateFormVMInitialProps(): InitialData {
        return {
            [FieldKey.TOPIC]: this.props.meetingTopic || this.getPresenterOrganizationNames(),
            [FieldKey.BROADCAST_DATE_TIME]: this.getFormattedBroadcastDateTime()
        };
    }

    private getPresenterOrganizationNames(): string {
        return this.props.presenterOrganizationList.map(({ name }) => name).join(', ');
    }

    private getFormattedBroadcastDateTime(): string {
        const format = `${DateFnsFormats.StringifiedDayMonthNumericDateYear}, ${this.dateTimeFormatter.getLocalTimeFormat()}`;
        const getStartMeetingDate = (): Date => {
            if (this.meetingStartDate) {
                if (this.props.timeZone) {
                    return DateTimeFormatter.applyTimeZoneToDate(this.meetingStartDate, this.props.timeZone);
                }
                return this.meetingStartDate;
            }

            return this.props.startDateTime;
        };

        return this.dateTimeFormatter.dateToFormattedString(getStartMeetingDate(), format);
    }

    private static handleApiException = (e: Error, title: string = 'Error'): void => {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    };

    private toApiDTO = (): IKnovioLiveStreamDTO => {
        const flyoutTabOptions = this.formDataCtrl.getValue(FieldKey.FLYOUT_TABS) as FlyoutTabOption[];
        const qaNotificationEmailList: string[] = getCommaSeparatedStringList(this.formDataCtrl.getValue(FieldKey.QA_NOTIFICATION_EMAIL) as string);
        const expirationDate = this.formDataCtrl.getValue(FieldKey.EXPIRATION_DATE);
        const expirationDateAPIString = expirationDate
            ? this.dateTimeFormatter.dateToFormattedString(expirationDate as Date, DateFnsFormats.StringifiedShortYearMonthDayDate)
            : null;

        return {
            title: this.formDataCtrl.getValue(FieldKey.TOPIC) as string,
            description: this.formDataCtrl.getValue(FieldKey.DESCRIPTION) as string,
            enableNeverlate: this.formDataCtrl.getValue(FieldKey.NEVER_LATE_NAVIGATION) as boolean,
            enableOverview: flyoutTabOptions.includes(FlyoutTabOption.Overview),
            enableQAndA: flyoutTabOptions.includes(FlyoutTabOption.QuestionsAndAnswers),
            enableSpeakers: flyoutTabOptions.includes(FlyoutTabOption.Speakers),
            layout: this.formDataCtrl.getValue(FieldKey.LAYOUT) as LayoutOption,
            qaNotificationEmailRecipients: qaNotificationEmailList.map(email => ({ email: email.trim() })),
            broadcastDate: this.apiDateConverter.dateToAPIString(this.props.startDateTime, this.props.timeZone),
            broadcastTimeZone: this.props.timeZone,
            standByMessage: transpileClassesToInlineStyles(this.formDataCtrl.getValue(FieldKey.STANDBY_MESSAGE) as string),
            expirationDate: expirationDateAPIString,
        };
    };

    @action
    public onSubmitKnovio = async (): Promise<void> => {
        const isCreateBehavior = this.behavior === KnovioLiveStreamVariant.CREATE;

        runInAction(() => {
            if (isCreateBehavior) {
                this.isCreatingKnovio = true;
            } else {
                this.isUpdatingKnovio = true;
            }
        });

        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
            runInAction(() => {
                if (isCreateBehavior) {
                    this.isCreatingKnovio = false;
                } else {
                    this.isUpdatingKnovio = false;
                }
            });
        } else {
            if (isCreateBehavior) {
                showNotification(
                    <FormattedMessage
                        id='oe.knovioLiveStream.knovioIsBeingCreated'
                        defaultMessage={`${KnovioLabel.OEStream} is being created`}
                        description='Notification text'
                    />
                );
                await this.onCreateKnovioHandler();
            } else {
                await this.onUpdateKnovioHandler();
            };
        }
    };

    @action
    public onUpdateKnovioHandler = async (): Promise<void> => {
       try {
        const data = {
            ...this.toApiDTO(),
            broadcastDate: this.apiDateConverter.dateToAPIString(this.meetingStartDate || this.props.startDateTime, this.props.timeZone),
        };

        await this.connectionsDAO.updateKnovioLiveStream({
            accountId: this.props.meetingApiParams.accountId,
            eventId: this.props.meetingApiParams.eventId,
            connectionId: this.meetingConnectionId,
            knovioPresentationId: this.knovioPresentationId
        }, data);

        this.updatingKnovioInProgress.add(this.knovioPresentationId);
        this.subscribeToUpdateKnovio();
        this.closeModal();
        this.knovioAfterUpdateCallback(data);
       } catch (error) {
            KnovioLiveStreamVM.handleApiException(error);
       }
    };

    private onCreateKnovioHandler = async (): Promise<void> => {
        try {
            const data = this.toApiDTO();
            const response = await this.connectionsDAO.createKnovioLiveStream(this.props.meetingApiParams, data);
            this.subscribeToCreateKnovio(response.meetingConnectionId);
            this.trackCreateKnovioMixPanel(this.props.meetingApiParams, data);
        } catch (error) {
            KnovioLiveStreamVM.handleApiException(error);
            runInAction(() => {
                this.isCreatingKnovio = false;
            });
        }
    };

    private subscribeToCreateKnovio = async (meetingConnectionId: number): Promise<void> => {
        try {
            const subscription = await subscribeAppsyncData<ICreateKnovioSubscriptionPayload>(
                onUpdateMeetingConnection,
                { meetingConnectionId: meetingConnectionId },
                data => {
                    const { value: { data: { onUpdateMeetingConnection: connectionDetails } } } = data;
                    this.afterCreateKnovioHandler(connectionDetails, subscription);
                },
            );
            this.subscriptions.add(subscription);
        } catch (error) {
            KnovioLiveStreamVM.handleApiException(error);
        }
    };

    private subscribeToUpdateKnovio = async (): Promise<void> => {
        try {
            const subscription = await subscribeAppsyncData<IOnSyncEventSubscriptionResponse>(
                onSyncEvent,
                { eventId: this.props.meetingApiParams.eventId },
                data => {
                    this.afterUpdateKnovioHandler(data, subscription);
                }
            );
            this.updatingKnovioSubscriptions.add(subscription);
        } catch (error) {
            KnovioLiveStreamVM.handleApiException(error);
        }
    };

    @action
    private afterUpdateKnovioHandler(data: IOnSyncEventSubscriptionResponse, subscription: IAppsyncSubscription) {
        const {value: { data: { onSyncEvent: { body, type } } } } = data;

        if (type !== OnSyncEventType.LIVE_PRESENTATION_UPDATED) return;

        subscription?.unsubscribe();
        this.updatingKnovioSubscriptions.delete(subscription);

        const parsedBody = JSON.parse(body);

        if (parsedBody.error && !parsedBody.successful) {
            KnovioLiveStreamVM.handleApiException(new Error(parsedBody.error));

            return;
        }

        this.updatingKnovioInProgress.delete(parsedBody.presentationId);

        showNotification(
            <FormattedMessage
                id='oe.knovioLiveStream.knovioIsUpdatedSuccessful'
                defaultMessage={`${KnovioLabel.OEStream} has been updated`}
                description='Notification text'
            />
        );
    }

    @action
    private afterCreateKnovioHandler = (connectionDetails: IConnectionDetailsDTO, subscription: IAppsyncSubscription): void => {
        subscription?.unsubscribe();
        this.subscriptions.delete(subscription);
        runInAction(() => {
            this.isCreatingKnovio = false;
        });
        if (connectionDetails.errorMessage) {
            KnovioLiveStreamVM.handleApiException(new Error(connectionDetails.errorMessage));
        } else {
            this.openKnovioConnectionForm(connectionDetails);
        }
    };

    private trackCreateKnovioMixPanel = (meetingParams: MeetingApiParams, data: IKnovioLiveStreamDTO): void => {
        const flyoutTabsTrackingStr = [
            data.enableOverview && 'Overview' || '',
            data.enableQAndA && 'Q&A' || '',
            data.enableSpeakers && 'Speakers' || '',
            !data.enableQAndA && !data.enableOverview && !data.enableSpeakers && 'None' || '',
        ].filter(Boolean).join(', ');

        const trackData = {
            'Account ID': meetingParams.accountId,
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Event ID': meetingParams.eventId,
            'Event Name': this.globalStorage.getEvent()?.name,
            'Meeting ID': meetingParams.meetingId || NOT_AVAILABLE_STRING,
            'Room ID': meetingParams.roomId || NOT_AVAILABLE_STRING,
            'Description': convertBoolTypeForMixPanel(!!data.description),
            'Neverlate': isTrackingFieldEnabled(data.enableNeverlate),
            'Flyout Tabs': flyoutTabsTrackingStr,
            'Layout': data.layout,
            'Stand By Message': convertBoolTypeForMixPanel(!!data.standByMessage),
            'Expiration Date': isTrackingFieldEnabled(this.formDataCtrl.getValue(FieldKey.EXPIRATION_DATE_ENABLED) as boolean),
        };

        this.trackingService.get().track(TRACKING_EVENTS.CREATE_KNOVIO_LIVE_PRESENTATION, trackData);
    };
}
